<template>
	<div class="width-100 all-projects-details pb-8">
		<ProjectDetailsCardGroup isVirtual class="mb-9" :projects="virtualProjects" />
		<template v-if="spaces.length > 0">
			<div class="project-header">Spaces</div>
			<div class="header-description mb-4 mt-1">Spaces are only visible to their collaborators. Workspace admins can see all spaces.</div>
			<ProjectDetailsCardGroup class="mb-9" :projects="spaces" />
		</template>
		<div v-if="collaboratedSpacesWithoutAccess.length > 0" class="project-header mb-4">Other shared spaces</div>
		<ProjectDetailsCardGroup class="mb-9" :projects="collaboratedSpacesWithoutAccess" />
		<div v-if="personalSpacesWithoutAccess.length > 0" class="project-header mb-4">Other personal spaces</div>
		<ProjectDetailsCardGroup class="mb-9" :projects="personalSpacesWithoutAccess" />
	</div>
</template>

<script>
import ProjectDetailsCardGroup from '@/components/Project/ProjectDetailsCardGroup.vue';
import { projectsList } from '@/store/modules/projects/getters';
import { getWorkflows } from '@/store/modules/workflows/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import ProjectService from '@/utils/projects-service';
import {
	ALL_PROJECTS_VIRTUAL_ID,
	ALL_PROJECTS_VIRTUAL_TITLE,
	PROJECTS_PERSONAL_ACCESS_TYPE,
	PROJECTS_WORKSPACE_ACCESS_TYPE,
	PROJECTS_SHARED_WITH_ME_VIRTUAL_ID,
	PROJECTS_SHARED_WITH_ME_VIRTUAL_TITLE,
} from '@/utils/projects.const';
export default {
	name: 'AllProjectDetails',
	props: {},
	components: {
		ProjectDetailsCardGroup,
	},
	computed: {
		...mapGettersShorthand({ projects: projectsList }),
		everythingWorkflowCount() {
			return this.projects.reduce((res, x) => res + (x.flows?.length || 0), 0) + this.sharedWorkflowsCount;
		},
		virtualProjects() {
			return [
				{
					id: ALL_PROJECTS_VIRTUAL_ID,
					title: ALL_PROJECTS_VIRTUAL_TITLE,
					description: 'Contains all items from all spaces',
					workflowsCount: this.everythingWorkflowCount,
					processesCount: 0,
				},
				{
					id: PROJECTS_SHARED_WITH_ME_VIRTUAL_ID,
					title: PROJECTS_SHARED_WITH_ME_VIRTUAL_TITLE,
					description: "Items that were shared with you as a user, where you don't have collaborator access",
					workflowsCount: this.sharedWorkflowsCount,
					processesCount: 0,
				},
			];
		},
		collaboratedSpacesWithoutAccess() {
			// only relevant to admins, who sees all spaces, even if they are not collaborating
			return this.projects.filter((x) => ProjectService.isSpaceWithoutAccess(x) && ProjectService.isWorkspaceProject(x));
		},
		personalSpacesWithoutAccess() {
			// only relevant to admins, who sees all spaces, even the private ones
			return this.projects.filter((x) => ProjectService.isSpaceWithoutAccess(x) && ProjectService.isPersonalProject(x));
		},
		allProjectsWithAccess() {
			return this.projects.filter((x) => !ProjectService.isSpaceWithoutAccess(x));
		},
		personalProjects() {
			return this.filterAndMapProjects(PROJECTS_PERSONAL_ACCESS_TYPE);
		},
		workspaceProjects() {
			return this.filterAndMapProjects(PROJECTS_WORKSPACE_ACCESS_TYPE);
		},
		spaces() {
			return [this.personalProjects?.[0] || null, ...(this.workspaceProjects || [])].filter((item) => item); //filter out if null;
		},
	},
	methods: {
		...mapActionsShorthand({ getWorkflows }),
		filterAndMapProjects(accessType) {
			return this.allProjectsWithAccess
				.filter((x) => x.access_type === accessType)
				.map((p) => ({ ...p, workflowsCount: p.flows?.length || 0, processesCount: 0, projectIcon: 'project-details' }));
		},
	},
	async mounted() {
		const firstPageOfSharedWorkflows = await this.getWorkflows({ filterBy: { shared_with_me: true } });
		this.sharedWorkflowsCount = firstPageOfSharedWorkflows?.count || 0;
	},
	data() {
		return {
			sharedWorkflowsCount: 0,
		};
	},
};
</script>

<style scoped lang="scss">
.all-projects-details {
	overflow-y: auto;
	height: 85%;
	.project-header {
		font-size: 18px;
		font-weight: 600;
	}
}
.header-description {
	font-size: 14px;
	color: var(--Colors-Neutral-Text-colorTextSecondary, #646e87);
	font-weight: 300;
}
</style>
